import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiChevronRight, mdiKeyOutline } from "@mdi/js";

import type { ListarUsuariosResponse } from "../../model/listarUsuariosResponse";
import { usePostUsuariosToken } from "../../config/swagger";
import { useToast } from "../../context/ToastContext";

import ListCard from "../ListCard";
import { Alert, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";

type UsuariosCardPropsContent = {
	data: ListarUsuariosResponse;
	skeleton?: never;
};

type UsuariosCardPropsSkeleton = {
	data?: never;
	skeleton: true;
};

type UsuariosCardProps = UsuariosCardPropsContent | UsuariosCardPropsSkeleton;

export default function UsuariosCard(props: UsuariosCardProps) {
	const navigate = useNavigate();
	const { handleToast } = useToast();

	const [tokenModal, setTokenModal] = useState(false);
	const [validade, setValidade] = useState(30);
	const [tokenGerado, setTokenGerado] = useState<string | null>(null);

	const mutationToken = usePostUsuariosToken({
		mutation: {
			onSuccess: (resp) => {
				if (resp.Result === 1 && resp.Data?.token) {
					setTokenGerado(resp.Data.token);
					handleToast("Token", "Token gerado com sucesso!", 5000, "success");
				} else {
					handleToast("Token", resp.Message || "Erro ao gerar token", 5000);
				}
			},
			onError: () => {
				handleToast("Token", "Erro ao gerar token", 5000, "danger");
			},
		},
	});

	function handleClick() {
		if (!props.skeleton && props.data) {
			navigate("/usuarioFormulario/" + props.data.id);
		}
	}

	function handleClickToken(e: React.MouseEvent) {
		e.stopPropagation();
		if (!props.skeleton && props.data) {
			setTokenModal(true);
			setTokenGerado(null);
			setValidade(30);
		}
	}

	function handleGerarToken() {
		mutationToken.mutate({
			data: {
				validade: validade,
			},
		});
	}

	function handleCopiarToken() {
		if (tokenGerado) {
			navigator.clipboard.writeText(tokenGerado);
			handleToast("Token", "Token copiado para a área de transferência!", 3000);
		}
	}

	let skeletonButton = "";
	if (props.skeleton) {
		skeletonButton = "skeleton skeleton-button";
	}

	return (
		<>
			<ListCard onClick={handleClick}>
				<ListCard.Content>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Nome</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.nome ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>E-mail</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.email ?? "..."}</ListCard.Text>
					</ListCard.Item>
				</ListCard.Content>
				<ListCard.Action>
					<Button variant="light" className={`me-2 ${skeletonButton}`} onClick={handleClickToken}>
						<Icon path={mdiKeyOutline} size={1} />
					</Button>
					<Icon path={mdiChevronRight} size={1} className={`text-muted m-2 ${skeletonButton}`} />
				</ListCard.Action>
			</ListCard>

			<Modal
				show={tokenModal}
				onHide={() => {
					setTokenModal(false);
					setTokenGerado(null);
				}}
				centered
				backdrop="static"
			>
				<Modal.Header closeButton>
					<Modal.Title>Gerar Token de Acesso</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{!tokenGerado ? (
						<>
							<Form.Group className="mb-3" controlId="validade">
								<Form.Label>Validade do Token</Form.Label>
								<InputGroup>
									<Form.Control type="number" min={1} max={365} value={validade} onChange={(e) => setValidade(Number(e.target.value))} disabled={mutationToken.isLoading} />
									<InputGroup.Text>dias</InputGroup.Text>
								</InputGroup>
								<Form.Text className="text-muted">Defina por quantos dias o token será válido (máximo 365 dias)</Form.Text>
							</Form.Group>
							<Alert variant="light">
								<small>
									<strong>Usuário:</strong> {props.data?.nome}
									<br />
									<strong>E-mail:</strong> {props.data?.email}
								</small>
							</Alert>
						</>
					) : (
						<>
							<Alert variant="light" className="text-center">
								Token gerado com sucesso!
							</Alert>
							<Form.Group className="mb-3 text-center" controlId="token">
								<InputGroup>
									<Form.Control type="text" value={tokenGerado} readOnly className="font-monospace" />
									<Button variant="outline-secondary" onClick={handleCopiarToken}>
										Copiar
									</Button>
								</InputGroup>
								<Form.Text className="text-muted text-center">
									Este token expira em {validade} {validade === 1 ? "dia" : "dias"}
								</Form.Text>
							</Form.Group>
							<Alert variant="warning" className="text-center">
								<small>
									<strong>Atenção:</strong> Guarde este token em local seguro. Por motivos de segurança, ele não será exibido novamente.
								</small>
							</Alert>
						</>
					)}
				</Modal.Body>
				<Modal.Footer>
					{!tokenGerado ? (
						<>
							<Button
								variant="secondary"
								onClick={() => {
									setTokenModal(false);
									setTokenGerado(null);
								}}
								disabled={mutationToken.isLoading}
							>
								Cancelar
							</Button>
							<Button variant="primary" onClick={handleGerarToken} disabled={mutationToken.isLoading}>
								{mutationToken.isLoading ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										Gerando...
									</>
								) : (
									"Gerar Token"
								)}
							</Button>
						</>
					) : (
						<Button
							variant="primary"
							onClick={() => {
								setTokenModal(false);
								setTokenGerado(null);
							}}
						>
							Fechar
						</Button>
					)}
				</Modal.Footer>
			</Modal>
		</>
	);
}
